import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "../../lib/utils";

/**
 * shadcn/ui Tooltip (Radix), in the AeroVect palette.
 *
 * Worth the dependency over a native `title`: the browser's own tooltip waits
 * about a second, cannot be styled, cannot wrap the two-sentence explanations
 * this page needs ("why is this field fixed"), and never appears on touch or
 * for a keyboard user. Radix opens on focus as well as hover and is announced.
 *
 * `Provider` is mounted once per view rather than per tooltip — that is what
 * makes the second tooltip in a row open instantly instead of re-waiting.
 */
const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-overlay max-w-[280px] rounded-md border border-line bg-white px-2.5 py-2 font-sans text-[12px] leading-snug text-ink shadow-md data-[state=delayed-open]:animate-in motion-reduce:animate-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = "TooltipContent";

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
